// ./src/components/features/Connectors/ConfigEditors/AwsSesConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Select,
  Group,
  Box,
  List,
  Button,
  ActionIcon,
  Paper,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconCloud,
  IconAlertTriangle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AwsSesConnectorConfig } from "@/types";
import { AccountEmailSelector } from "@/components/features";

export interface AwsSesConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// AWS regions that support SES
const AWS_SES_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "af-south-1", label: "Africa (Cape Town)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-northeast-3", label: "Asia Pacific (Osaka)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "eu-south-1", label: "Europe (Milan)" },
  { value: "me-south-1", label: "Middle East (Bahrain)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

// Default empty configuration
const DEFAULT_CONFIG: AwsSesConnectorConfig = {
  accessKeyId: "",
  secretAccessKey: "",
  region: "",
  fromAccountEmailId: "",
  configurationSetName: "",
  messageTags: {},
};

/**
 * Parse and validate AWS SES connector configuration
 */
const parseAwsSesConfig = (jsonString: string): AwsSesConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  // Ensure objects exist
  if (!parsed.messageTags) parsed.messageTags = {};
  return parsed;
};

export const AwsSesConnectorConfigEditor: React.FC<
  AwsSesConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AwsSesConnectorConfig>(() =>
    parseAwsSesConfig(value)
  );

  // Local state for adding new message tag
  const [newTagKey, setNewTagKey] = useState("");
  const [newTagValue, setNewTagValue] = useState("");

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAwsSesConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AwsSesConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessKeyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKeyId: e.target.value });
  };

  const handleSecretAccessKeyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, secretAccessKey: e.target.value });
  };

  const handleRegionChange = (val: string | null) => {
    if (val) {
      updateConfig({ ...config, region: val });
    }
  };

  const handleFromAccountEmailIdChange = (accountEmailId: string | null) => {
    updateConfig({ ...config, fromAccountEmailId: accountEmailId || "" });
  };

  const handleConfigurationSetNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, configurationSetName: e.target.value });
  };

  // Message tags management
  const handleAddMessageTag = () => {
    const key = newTagKey.trim();
    const val = newTagValue.trim();
    if (key && val) {
      const messageTags = { ...(config.messageTags || {}), [key]: val };
      updateConfig({ ...config, messageTags });
      setNewTagKey("");
      setNewTagValue("");
    }
  };

  const handleRemoveMessageTag = (key: string) => {
    const messageTags = { ...(config.messageTags || {}) };
    delete messageTags[key];
    updateConfig({ ...config, messageTags });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconCloud size={16} />} color="orange" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://console.aws.amazon.com/ses/"
              target="_blank"
              rel="noopener noreferrer"
            >
              AWS SES Console
            </Anchor>
          </List.Item>
          <List.Item>
            Verify your sender email or domain in{" "}
            <strong>Verified identities</strong>
          </List.Item>
          <List.Item>
            Create IAM credentials with <strong>ses:SendEmail</strong>{" "}
            permission
          </List.Item>
          <List.Item>
            If in sandbox mode, also verify recipient emails
          </List.Item>
        </List>
      </Alert>

      {/* Access Key ID Field */}
      <TextInput
        label="Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        description="AWS IAM Access Key ID"
        value={config.accessKeyId}
        onChange={handleAccessKeyIdChange}
        disabled={disabled}
        required
        size="md"
        autoComplete="off"
      />

      {/* Secret Access Key Field */}
      <TextInput
        label="Secret Access Key"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        description="AWS IAM Secret Access Key"
        value={config.secretAccessKey}
        onChange={handleSecretAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Region Field */}
      <Select
        label="Region"
        placeholder="Select AWS region"
        description="AWS region where SES is configured"
        data={AWS_SES_REGIONS}
        value={config.region}
        onChange={handleRegionChange}
        disabled={disabled}
        required
        size="md"
        searchable
        allowDeselect={false}
      />

      {/* From Account Email Field */}
      <AccountEmailSelector
        label="Sender Email"
        placeholder="Select a verified email"
        description="Choose a confirmed account email to use as the sender address"
        value={config.fromAccountEmailId}
        onChange={handleFromAccountEmailIdChange}
        disabled={disabled}
        required
        size="md"
        confirmedOnly
        emptyText="No confirmed emails available"
      />

      {/* Configuration Set Name Field */}
      <TextInput
        label="Configuration Set (Optional)"
        placeholder="my-configuration-set"
        description="For tracking opens, clicks, bounces, and complaints"
        value={config.configurationSetName || ""}
        onChange={handleConfigurationSetNameChange}
        disabled={disabled}
        size="md"
      />

      {/* Configuration Set Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          What is a Configuration Set?
        </Text>
        <Text size="xs">
          Configuration sets let you track email events (opens, clicks, bounces)
          and publish them to CloudWatch, SNS, Firehose, or Pinpoint. Create one
          in{" "}
          <Anchor
            href="https://console.aws.amazon.com/ses/home#/configuration-sets"
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
          >
            SES → Configuration sets
          </Anchor>
          .
        </Text>
      </Alert>

      {/* Message Tags Section */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Message Tags (Optional)
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Key-value pairs for categorization and analytics. Values support{" "}
          <code>{"{{varName}}"}</code> placeholders.
        </Text>

        {/* Existing message tags */}
        {Object.keys(config.messageTags || {}).length > 0 && (
          <Stack gap="xs" mb="sm">
            {Object.entries(config.messageTags || {}).map(([key, val]) => (
              <Group key={key} gap="xs">
                <Paper withBorder p="xs" style={{ flex: 1 }} bg="gray.0">
                  <Text size="sm">
                    <strong>{key}:</strong> {val}
                  </Text>
                </Paper>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => handleRemoveMessageTag(key)}
                  disabled={disabled}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}

        {/* Add new message tag */}
        <Group gap="xs" align="flex-end">
          <TextInput
            placeholder="Key (e.g., campaign)"
            value={newTagKey}
            onChange={(e) => setNewTagKey(e.target.value)}
            disabled={disabled}
            size="sm"
            style={{ flex: 1 }}
          />
          <TextInput
            placeholder="Value (e.g., {{formId}})"
            value={newTagValue}
            onChange={(e) => setNewTagValue(e.target.value)}
            disabled={disabled}
            size="sm"
            style={{ flex: 2 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddMessageTag();
              }
            }}
          />
          <Button
            size="sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddMessageTag}
            disabled={disabled || !newTagKey.trim() || !newTagValue.trim()}
          >
            Add
          </Button>
        </Group>
      </Box>

      {/* Sandbox Mode Warning */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="yellow"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          SES Sandbox Mode:
        </Text>
        <Text size="sm">
          New SES accounts are in sandbox mode by default. In sandbox mode, you
          can only send to verified email addresses. To send to any recipient,{" "}
          <Anchor
            href="https://docs.aws.amazon.com/ses/latest/dg/request-production-access.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            request production access
          </Anchor>
          .
        </Text>
      </Alert>

      {/* IAM Policy Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Required IAM Policy:
        </Text>
        <Text
          size="xs"
          component="pre"
          style={{
            backgroundColor: "var(--mantine-color-gray-1)",
            padding: "8px",
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          {`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": ["ses:SendEmail", "ses:SendRawEmail"],
    "Resource": "*"
  }]
}`}
        </Text>
      </Alert>

      {/* How it works */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            This connector configures the AWS SES connection settings
          </List.Item>
          <List.Item>
            Recipients, subject, and body are configured in the workflow
          </List.Item>
          <List.Item>
            Each recipient receives an individual email for personalization
          </List.Item>
          <List.Item>
            Message tags are included for CloudWatch/SNS event tracking
          </List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
